<template>
  <v-container fluid class="scroll-y fullscreen accent" pa-0>
    <v-card
      class="mx-auto"
      flat
      :loading="loading"
      loader-height="10"
      tile
    >
      <v-toolbar color="secondary" dark flat>
        <v-toolbar-title>{{ pageTitleFull }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <downloadexcel
          class="btn mr-2"
          :fetch="fetchData"
          :fields="json_fields"
          :before-generate="startDownload"
          :before-finish="finishDownload"
          name="export.xls"
        >
          <v-btn
            color="#ffffff"
            class="primary--text"
            tile
            depressed
            large
            :loading="downloadLoading"
          >
            Export
          </v-btn>
        </downloadexcel>
        <v-btn color="primary" :to="{ name: 'New Task' }" tile depressed large>
          <v-icon dark>mdi-plus</v-icon>
          Add New
        </v-btn>
        <v-icon class="ml-4" large dark @click="drawer = !drawer">mdi-magnify</v-icon>
      </v-toolbar>
    </v-card>

    <!-- Card that contains the tabs and the data table -->
    <v-card flat tile class="mx-auto overflow-hidden" min-height="calc(-128px + 100vh)">
      <!-- Tabs for filtering tasks by system -->
      <v-tabs
        v-model="activeTab"
        background-color="primary"
        dark
        @change="onTabChange"
      >
        <!-- Tab for showing all systems -->
        <v-tab :value="'all'">
          All Tasks
        </v-tab>
        <!-- Create a tab for each system (from the Vuex store) -->
        <v-tab v-for="system in systems" :key="system.id" :value="system.id">
          {{ system.title }}
        </v-tab>
      </v-tabs>

      <!-- Data table displaying the tasks -->
      <v-data-table
        :page="page"
        :pageCount="numberOfPages"
        :headers="headers"
        :items="tasks"
        :options.sync="options"
        :server-items-length="totalTasks"
        :footer-props="{ 'items-per-page-options': [50, 100, 500] }"
        class="elevation-0"
        :expanded.sync="expanded"
        item-key="id"
        show-expand
        :loading="loading"
      >
        <!-- (Your existing slots/templates for task actions, printing, etc.) -->
        <template v-slot:item.Tasks="{ item }">
            <v-btn
                small
                depressed
                color="secondary"
                tile
                :to="`/task/edit/${item.id}`"
            >
                <v-icon color="white" small>
                mdi-pencil
                </v-icon>
            </v-btn>
            <v-btn small depressed color="primary" tile @click="deleteItem(item)">                  
                <v-icon
                small
                >
                mdi-delete
                </v-icon>  
            </v-btn>
            <!-- New Comments Button -->
            <v-btn
              small
              depressed
              color="info"
              tile
              :to="`/task/edit/${item.id}?tab=comments`"
            >
              <v-icon color="white" small>
                mdi-comment-text-outline
              </v-icon>
            </v-btn>
        </template>
        <!-- Custom slot for the Task Title column -->
        <template v-slot:item.title="{ item }">
          <router-link :to="`/task/edit/${item.id}`" class="clickable-title">
            {{ item.title }}
          </router-link>
        </template>
        <!-- Other slot templates remain unchanged -->
        <template v-slot:item.system="props">
        <v-chip
            :color="systemChipColors[ systemsMap[props.item.system_id] ] || 'grey'"
            dark
            small
        >
            {{ systemsMap[props.item.system_id] || 'Unknown System' }}
        </v-chip>
        </template>
        <template v-slot:item.status="props">
            <v-chip 
                :color="statusChipColors[statusesMap[props.item.status_id]] || 'grey'" 
                dark 
                small
            >
                {{ statusesMap[props.item.status_id] || 'Unknown Status' }}
            </v-chip>
        </template>
        <template v-slot:item.assignee="props">
          <div v-if="props.item.assignee && props.item.assignee.length">
            <v-tooltip
              v-for="(assigneeId, index) in props.item.assignee"
              :key="index"
              bottom
              open-on-hover
            >
              <template v-slot:activator="{ on, attrs }">
                <v-avatar
                  v-bind="attrs"
                  v-on="on"
                  size="34"
                  class="ma-1"
                  :color="userColorsMap[assigneeId]"
                >
                  <span class="white--text text-caption">
                    {{ userInitialsMap[assigneeId] }}
                  </span>
                </v-avatar>
              </template>
              <span>{{ usersMap[assigneeId] }}</span>
            </v-tooltip>
          </div>
          <div v-else>
            <span>N/A</span>
          </div>
        </template>
        <template v-slot:item.user="props">
          <div v-if="props.item.user">
            <v-tooltip bottom open-on-hover>
              <template v-slot:activator="{ on, attrs }">
                <v-avatar
                  v-bind="attrs"
                  v-on="on"
                  size="34"
                  class="ma-1"
                  :color="userColorsMap[props.item.user.id]"
                >
                  <span class="white--text text-caption">
                    {{ userInitialsMap[props.item.user.id] }}
                  </span>
                </v-avatar>
              </template>
              <span>{{ props.item.user.name }}</span>
            </v-tooltip>
          </div>
          <div v-else>
            <span>Deleted User</span>
          </div>
        </template>
        <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-5">
                <strong>Details:</strong> <div class="details" v-html="item.details"></div>
                <br/><br/>
                <strong>Created at:</strong> {{ item.created_at }}
            </td>
        </template>
      </v-data-table>
    </v-card>
    <v-dialog v-model="dialogDelete" max-width="500px">
        <v-card color="red">
            <v-card-title class="subtitle-2 justify-center white--text">Are you sure to delete this item?</v-card-title>
            <v-card-actions class="red darken-4">
                <v-spacer></v-spacer>
                <v-btn tile color="red--text" @click="deleteItemConfirm" :loading="loading">Yes</v-btn>
                <v-btn tile color="white darken-1" text @click="closeDelete">Cancel</v-btn>
                <v-spacer></v-spacer>
            </v-card-actions>
        </v-card>
    </v-dialog>
    <!-- The FilterTasks component remains unchanged -->
    <FilterTasks
      :show="drawer"
      :filter="filter"
      :routekey="routeKey"
      @update-id="updateId"
      @update-drawer="updateDrawer"
      @update-status="updateStatus"
      @update-daterange="updateDaterange"
      @update-system="updateSystem"
      @update-user="updateUser"
      @submit-search="submitSearch"
    />
  </v-container>
</template>


<script>
import axios from 'axios'
import FilterTasks from '../../components/FilterTasks.vue'
import { mapGetters } from 'vuex'
import downloadexcel from "vue-json-excel"
import print from 'vue-print-nb'

export default {
  data() {
    return {
      drawer: false,
      loading: true,
      tasks: [], // renamed Tasks to tasks for consistency
      totalTasks: 0,
      numberOfPages: 0,
      // Initialize options with a default of 50 items per page
      options: {
        page: 1,
        itemsPerPage: 50,
        sortBy: [],
        sortDesc: []
      },
      pageTitle: '',
      apiUrl: '',
      routeKey: '',
      headers: [
        { text: 'Task Title', value: 'title', sortable: false },
        { text: 'Category', value: 'system', sortable: false },
        { text: 'Assignee', value: 'assignee', sortable: false },
        { text: 'Status', value: 'status', sortable: false },
        { text: 'Added By', value: 'user', sortable: false },
        { text: 'Tasks', value: 'Tasks', sortable: false, align: 'center' },
        { text: 'Other', value: 'data-table-expand' },
      ],
      filter: {
        id: "",
        user: [],
        dates: [],
        system: [], // initially no system filter
        status: [1,2]
      },
      dialogDelete: false,
      editedItem: {
        id: '',
      },
      defaultItem: {
        id: '',
      },
      downloadLoading: false,
      json_fields: {
        'Title': 'title',
        'Date/Time of Task': 'created_at',
        'Employee Logging Task': 'user',
        'Category': 'system',
        'Details of Task': 'details',
        'Resolution': 'resolution',
        'Status': 'status',
        'Assignee': 'assignee',
      },
      printObj: {
        asyncUrl (resolve) {
          setTimeout(() => {
            resolve('https://tasks.bigcitymarketing.co/api/task/print')
          }, 2000)
        },
        preview: true,
        previewTitle: 'Print',
        popTitle: 'Print',
        previewPrintBtnLabel: 'Print',
        beforeOpenCallback() {
          console.log('test')
        },
        openCallback() {
          console.log('test1')
        },
        closeCallback() {
          console.log('test2')
        }
      },
      // New property to track the active tab.
      // Use 'all' for no filter; otherwise, the system id.
      activeTab: 'all',
    }
  },
  created() {
    this.pageTitle = this.$route.meta.title
    this.routeKey = this.$route.meta.key
    if (this.routeKey === 'new') {
      this.filter.status = 'confirmed'
    }
  },
  components: {
    FilterTasks,
    downloadexcel
  },
  mounted() {
    this.getTasks()
  },
  directives: {
    print   
  },
  computed: {
    ...mapGetters({
      systems: 'data/systems',
      statuses: 'data/task_status',
      users: 'data/users'
    }),
    pageTitleFull() {
      return this.loading ? 'Loading...' : this.pageTitle
    },
    usersMap() {
      return this.users.reduce((map, user) => {
        map[user.id] = user.name;
        return map;
      }, {});
    },
    userInitialsMap() {
      return this.users.reduce((map, user) => {
        // Use the user's initials if available; otherwise, compute them from the name.
        if (user.initials) {
          map[user.id] = user.initials;
        } else if (user.name) {
          map[user.id] = user.name
            .split(' ')
            .map(word => word.charAt(0))
            .join('');
        } else {
          map[user.id] = 'NA';
        }
        return map;
      }, {});
    },
    userColorsMap() {
      // Define an array of colors. Feel free to change these values.
      const colors = [
        'red lighten-2',
        'blue lighten-2',
        'green lighten-2',
        'amber lighten-2',
        'purple lighten-2',
        'pink lighten-2',
        'deep-orange lighten-2',
        'teal lighten-2'
      ];
      return this.users.reduce((map, user, index) => {
        // You can use index-based assignment, or calculate a hash from user.name for more randomness.
        map[user.id] = colors[index % colors.length];
        return map;
      }, {});
    },
    systemsMap() {
      return this.systems.reduce((map, system) => {
        map[system.id] = system.title;
        return map;
      }, {});
    },
    statusesMap() {
      return this.statuses.reduce((map, status) => {
        map[status.id] = status.title;
        return map;
      }, {});
    },
    statusChipColors() {
        return {
        'Pending': 'yellow',
        'In Progress': 'blue',
        'Done': 'green'
        }
    },
    systemChipColors() {
        return {
            'CCIP': '#FF7F50', // Coral
            'Current Tasks': '#6495ED', // Cornflower Blue
            'Payroll': '#FFD700', // Gold
            'Other': '#90EE90', // Light Green
            'System E': 'Unknown System'  // Hot Pink
        }
    }
  },
  watch: {
    options: {
      handler() {
        this.getTasks();
      },
      deep: true,
    },
  },
  methods: {
    getTasks() {
      this.loading = true
      const { page, itemsPerPage } = this.options
      axios.get(`tasks?size=${itemsPerPage}&page=${page}`, {
        params: this.filter
      })
      .then(response => {
        this.loading = false
        this.tasks = response.data.data
        this.numberOfPages = response.data.last_page
        this.totalTasks = response.data.total
        this.$nextTick()
      })
    },
    deleteItem(item) {
      this.editedItem = Object.assign({}, item)
      this.dialogDelete = true
    },
    deleteItemConfirm() {
      this.loading = true
      axios.delete('tasks/' + this.editedItem.id)
        .then(() => {
          this.closeDelete()
          this.getTasks()
          this.$nextTick()
        })
    },
    closeDelete() {
      this.dialogDelete = false
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
      })
    },
    updateDrawer(val) {
      this.drawer = val
    },
    updateId(val) {
      this.filter.id = val
    },
    updateSystem(val) {
      this.filter.system = val
      this.getTasks()
    },
    updateUser(val) {
      this.filter.user = val
      this.getTasks()
    },
    updateStatus(val) {
      this.filter.status = val
      this.getTasks()
    },
    updateDaterange(val) {
      this.filter.daterange = val
    },
    submitSearch() {
      this.getTasks()
    },
    async fetchData() {
      const response = await axios.get('export/tasks', {
        params: this.filter
      })
      console.log(response)
      return response.data
    },
    startDownload() {
      this.downloadLoading = true
    },
    finishDownload() {
      this.downloadLoading = false
    },
    // Method called when the tab selection changes.
    onTabChange(value) {
      if (value === 'all') {
        this.filter.system = []
      } else {
        // If your API expects an array of system IDs, you might use:
        // this.filter.system = [value]
        // Otherwise, if a single value is acceptable, simply:
        this.filter.system = value
      }
      this.getTasks()
    }
  }
}
</script>

<style lang="sass" scoped>
.theme--light 
    .v-content
        background: #ffffff

.fullscreen
    background: #fff
    height: 100%

.w-100
    width: 100%
    .v-btn 
        width: 100%

.v-list-item__Task
    .v-btn
        &:not(.v-btn--round)
            &.v-size--small
                height: 42px

.v-chip 
    border-radius: 0!important

.customers-count
    font-size: 14px

.v-data-table 
    .v-data-table__mobile-row
        min-height: 32px!important

a.clickable-title
    font-weight: bold
    font-size: medium
    text-decoration: none
    line-height: 1em
    color: #212121
</style>


<style lang="sass">
.v-data-table 
  .v-data-table__mobile-row
    min-height: 32px!important

tr
  &.v-data-table__mobile-table-row 
    padding: 20px 0 20px !important
    display: block !important
    border-bottom: 6px solid #e5e5e5

.theme--light.v-data-table 
  tbody 
    tr:not(:last-child) 
      td:last-child
        border-bottom: none !important

thead.v-data-table-header.v-data-table-header-mobile
  display: none

.v-tabs:not(.v-tabs--vertical) .v-tab
  font-size: 18px
  letter-spacing: 0
  text-transform: capitalize

.v-data-table th
  font-size: 1rem
  height: 48px
  color: #df2027 !important
</style>